import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity } = this.getCurrentValues();
    if (max === null) return null;
    const originalQuantity = Number(this.refs.quantityInput.defaultValue) || 0;
    return Math.max(originalQuantity, max - Math.max(0, cartQuantity - originalQuantity));
  }

  hasBoardCart() {
    return Boolean(this.closest('cart-items-component')?.querySelector?.('[data-board-bundle-id]:not([data-board-bundle-id=""])'));
  }

  notifyUnavailableQuantity(quantity, event) {
    const effectiveMax = this.getEffectiveMax();
    const helper = window.HalfpipedBoardCartQuantity;
    if (!Number.isFinite(quantity) || !helper?.notifyQuantityLimit || !this.hasBoardCart() || effectiveMax === null || quantity <= effectiveMax) return false;
    event.preventDefault();
    const { max, cartQuantity } = this.getCurrentValues();
    this.refs.quantityInput.value = this.refs.quantityInput.defaultValue;
    this.syncQuantitySelect();
    this.updateButtonStates();
    return helper?.notifyQuantityLimit(helper.quantityLimitError(max, {
      stockLimited: this.refs.quantityInput.dataset?.stockLimited === 'true', cartQuantity,
      item: helper.itemFromCartRow?.(this.closest('[data-key]')),
    })) || false;
  }

  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const { value, step } = this.getCurrentValues();
    if (!this.notifyUnavailableQuantity(value + step, event)) super.increaseQuantity(event);
  }

  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!this.notifyUnavailableQuantity(Number(event.target.value), event)) super.setQuantity(event);
  }

  setQuantityFromSelect(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!this.notifyUnavailableQuantity(Number(event.target.value), event)) super.setQuantityFromSelect(event);
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const row = this.closest('[data-board-bundle-id]');
    const boardPart = Boolean(row?.dataset.boardBundleId);
    const fixedBoardChoice = boardPart && row.dataset.boardExtraAllowed !== 'true';

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min || boardPart;
    plusButton.disabled = fixedBoardChoice || (!this.hasBoardCart() && effectiveMax !== null && value >= effectiveMax);
    if (boardPart) plusButton.setAttribute('aria-label', 'Add an extra item outside this board');
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
